import {
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	useMediaQuery,
	useTheme,
	Skeleton,
	Grid,
	Card,
	CardContent,
	CardActions,
	Box,
} from '@mui/material';
import React from 'react';
import {
	CerebroDrawer,
	CerebroExpandingText,
	CerebroHeading,
	CerebroLabel,
	CerebroLink,
	CerebroStatusIndicatorTableCell,
	CerebroStyledBox,
	CerebroText,
	CerebroTextButton,
	StatusIndicatorColor,
} from '../../shared/components';
import {
	CanAccessDiligenceRequestResolution,
	CurrentUser,
	DataRoomDiligenceRequest,
	ShowDiligenceRequestFiles,
} from '../../shared/models';
import { DateUtils, EnumUtils, FileUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { DiligenceRequestActionButton } from './DiligenceRequestActionButton';

interface DiligenceRequestsListProps {
	dataRoomGuid: string | undefined;
	isLoading: boolean;
	items: DataRoomDiligenceRequest[] | undefined;
	currentUser: CurrentUser;
	isTeamMember: boolean;
	tab: string;
	onEdit: (request: DataRoomDiligenceRequest) => void;
	onDelete: (request: DataRoomDiligenceRequest) => void;
	onAssociate: (request: DataRoomDiligenceRequest) => void;
}

export const DiligenceRequestsList: React.FC<DiligenceRequestsListProps> = ({
	dataRoomGuid,
	isLoading,
	items,
	currentUser,
	isTeamMember,
	tab,
	onEdit,
	onDelete,
	onAssociate,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [viewRequestDetails, setViewRequestDetails] = React.useState<DataRoomDiligenceRequest | null>(null);
	const isLender = currentUser.isBankUser || currentUser.isTeamMemberUser;

	const getStatusColor = (request: DataRoomDiligenceRequest): StatusIndicatorColor => {
		return request.isCompleted ? 'Green' : request.isWithdrawn ? 'Red' : 'Yellow';
	};

	const getStatusTooltip = (request: DataRoomDiligenceRequest): string => {
		return request.isCompleted ? 'Completed' : request.isWithdrawn ? 'Withdrawn' : 'Pending';
	};

	return (
		<>
			{isMobile ? (
				<Grid container spacing={2} alignItems="stretch">
					{isLoading ? (
						<>
							<Skeleton height={120} />
							<Skeleton height={120} />
							<Skeleton height={120} />
						</>
					) : (
						<>
							{items?.map(request => {
								const canEdit =
									!currentUser.isBankUser ||
									(currentUser.isBankUser && request.createdByGuid == currentUser.userGuid);

								return (
									<Grid key={request.guid + tab} item xs={12}>
										<Card
											elevation={3}
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												flexDirection: 'column',
												height: '100%',
												marginBottom: 2,
											}}
										>
											<CardContent sx={{ paddingBottom: '0 !important' }}>
												<Grid container sx={{ marginBottom: 2 }}>
													<Grid item xs={12} sx={{ display: 'flex' }}>
														<CerebroLabel sx={{ fontWeight: 'bold', alignSelf: 'center' }}>
															{request.title}
														</CerebroLabel>
													</Grid>
												</Grid>
												{!isLender && request.dataRoomUser && (
													<CerebroText>
														{request.dataRoomUser} ({request.bankName})
													</CerebroText>
												)}
												<CerebroText>
													{EnumUtils.GetDiligenceRequestTypeDescription(
														request.diligenceType
													)}
												</CerebroText>
												<CerebroText>
													Created on{' '}
													{DateUtils.FormatStringAsShortDate(request.createdOn?.toString())}
												</CerebroText>
												{request.createdByUser && !isLender && (
													<CerebroText>Requested by {request.createdByUser}</CerebroText>
												)}
												{!isLender && (
													<CerebroText>Created by {request.createdByUser}</CerebroText>
												)}
												{request.updatedByUser && !isLender && (
													<CerebroText>Updated by {request.updatedByUser}</CerebroText>
												)}
												{request.isCompleted ? (
													<CerebroText>
														{!isLender ? (
															<>Completed by {request.updatedByUser} on </>
														) : (
															<>Completed: </>
														)}
														{DateUtils.FormatStringAsShortDate(
															request.completedOn?.toString()
														)}
													</CerebroText>
												) : (
													<CerebroText>Pending</CerebroText>
												)}
												{request.isWithdrawn && currentUser.isEmployeeUser && (
													<CerebroText>Withdrawn</CerebroText>
												)}

												{CanAccessDiligenceRequestResolution(currentUser, request) && (
													<>
														<CerebroText>Response</CerebroText>
														<CerebroText>
															{request.diligenceType == 'Question'
																? request.resolution
																: `${request.files.length} files have been associated with this request.`}
														</CerebroText>
													</>
												)}
											</CardContent>
											<CardActions
												sx={theme => ({
													alignItems: 'flex-end',
													backgroundColor: theme.palette.gray.background,
													borderTop: `1px solid ${theme.palette.divider}`,
													justifyContent: 'space-between',
												})}
											>
												{canEdit && (
													<CerebroTextButton size="small" onClick={() => onEdit(request)}>
														Edit
													</CerebroTextButton>
												)}
												<CerebroTextButton
													size="small"
													onClick={() => setViewRequestDetails(request)}
												>
													View Details
												</CerebroTextButton>
												{canEdit && (
													<CerebroTextButton size="small" onClick={() => onDelete(request)}>
														Delete
													</CerebroTextButton>
												)}
											</CardActions>
										</Card>
									</Grid>
								);
							})}
							{items?.length == 0 && (
								<CerebroStyledBox>
									<CerebroText sx={{ marginBottom: 2 }}>
										There are currently no diligence requests.
									</CerebroText>
								</CerebroStyledBox>
							)}
						</>
					)}
				</Grid>
			) : (
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: '5px', padding: '0px' }}></TableCell>
								<TableCell sx={{ width: '35%' }}>Title</TableCell>
								<TableCell sx={{ width: '35%' }}>Response</TableCell>
								<TableCell sx={{ width: '20%' }}>Additional Data</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<>
									<TableRow>
										<TableCell colSpan={5}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={5}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={5}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
								</>
							) : (
								<>
									{items?.map(request => (
										<TableRow key={request.guid + tab}>
											<CerebroStatusIndicatorTableCell
												colorType={getStatusColor(request)}
												tooltip={getStatusTooltip(request)}
											/>
											<TableCell>
												<CerebroExpandingText text={request.title} limit={200} isHtml={false} />
											</TableCell>
											<TableCell>
												{CanAccessDiligenceRequestResolution(currentUser, request) && (
													<CerebroExpandingText
														text={request.resolution}
														limit={200}
														isHtml={true}
													/>
												)}
												{ShowDiligenceRequestFiles(request) && (
													<>
														{request.files.map(file => (
															<CerebroText key={file.guid}>
																<CerebroLink
																	href={CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificFileDownload(
																		dataRoomGuid,
																		request.guid,
																		file.guid
																	)}
																	target="_blank"
																	rel="noreferrer"
																	variant="link"
																>
																	{FileUtils.getFileTypeIcon(file.fileName)}{' '}
																	{file.title}
																</CerebroLink>
															</CerebroText>
														))}
													</>
												)}
											</TableCell>
											<TableCell>
												{request.lenderName && !isLender && (
													<CerebroText variant="small">
														Lender: {request.lenderName}
													</CerebroText>
												)}
												{request.bankName && !isLender && (
													<CerebroText variant="small">
														Institution: {request.bankName}
													</CerebroText>
												)}
												<CerebroText variant="small">
													Type:{' '}
													{EnumUtils.GetDiligenceRequestTypeDescription(
														request.diligenceType
													)}
												</CerebroText>
												<CerebroText variant="small">
													Created:{' '}
													{DateUtils.FormatStringAsShortDate(request.createdOn?.toString())}
												</CerebroText>
												{request.isCompleted && (
													<CerebroText variant="small">
														{!isLender ? (
															<>Completed by {request.updatedByUser} on </>
														) : (
															<>Completed: </>
														)}
														{DateUtils.FormatStringAsShortDate(
															request.completedOn?.toString()
														)}
													</CerebroText>
												)}
											</TableCell>
											<TableCell>
												<DiligenceRequestActionButton
													currentUser={currentUser}
													isTeamMember={isTeamMember}
													request={request}
													onEdit={onEdit}
													onDelete={onDelete}
													onViewDetails={setViewRequestDetails}
													onAssociate={onAssociate}
												/>
											</TableCell>
										</TableRow>
									))}
									{items?.length == 0 && (
										<TableRow>
											<TableCell colSpan={5}>
												<CerebroText sx={{ marginTop: 3, marginBottom: 3 }}>
													There are currently no diligence requests.
												</CerebroText>
											</TableCell>
										</TableRow>
									)}
								</>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<CerebroDrawer open={viewRequestDetails != null} onClose={() => setViewRequestDetails(null)}>
				<CerebroHeading sx={{ marginBottom: 3 }}>{viewRequestDetails?.title}</CerebroHeading>

				{!isLender && viewRequestDetails?.dataRoomUser && (
					<>
						<CerebroLabel>Lender</CerebroLabel>
						<CerebroText sx={{ marginBottom: 2 }}>
							{viewRequestDetails?.dataRoomUser} ({viewRequestDetails?.bankName})
						</CerebroText>
					</>
				)}

				<CerebroLabel>Additional Details / Context</CerebroLabel>
				<CerebroText sx={{ marginBottom: 2 }}>{viewRequestDetails?.details}</CerebroText>

				<CerebroLabel>Created</CerebroLabel>
				<CerebroText sx={{ marginBottom: 2 }}>
					{!isLender && <>{viewRequestDetails?.createdByUser} on </>}
					{DateUtils.FormatStringAsShortDate(viewRequestDetails?.createdOn?.toString())}
				</CerebroText>

				{CanAccessDiligenceRequestResolution(currentUser, viewRequestDetails) && (
					<>
						<CerebroLabel>Response</CerebroLabel>
						<Box
							sx={{ marginBottom: 2 }}
							dangerouslySetInnerHTML={{
								__html: viewRequestDetails?.resolution ?? '',
							}}
						></Box>
					</>
				)}

				{!isLender && (
					<>
						<CerebroLabel>Any files for this diligence request will be saved to:</CerebroLabel>
						<CerebroText sx={{ marginBottom: 2 }}>{viewRequestDetails?.folder?.name}</CerebroText>
					</>
				)}

				{ShowDiligenceRequestFiles(viewRequestDetails) && (
					<>
						<CerebroLabel>Files</CerebroLabel>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>File</TableCell>
										<TableCell>Date</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{viewRequestDetails?.files.map(file => {
										return (
											<TableRow key={file.guid}>
												<TableCell>
													<CerebroLink
														href={CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificFileDownload(
															dataRoomGuid,
															viewRequestDetails.guid,
															file.guid
														)}
														target="_blank"
														rel="noreferrer"
														variant="link"
													>
														{FileUtils.getFileTypeIcon(file.fileName)} {file.title}
													</CerebroLink>
												</TableCell>
												<TableCell>
													{DateUtils.FormatStringAsShortDate(file.createdOn.toString())}
												</TableCell>
											</TableRow>
										);
									})}
									{viewRequestDetails?.links.map(link => {
										return (
											<TableRow key={link.guid}>
												<TableCell>{link.document.name}</TableCell>
												<TableCell>
													{DateUtils.FormatStringAsShortDate(
														link.document.documentDate.toString()
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}

				{viewRequestDetails && viewRequestDetails.isCompleted && (
					<CerebroText sx={{ marginTop: -2, marginBottom: 2 }}>
						{!isLender ? <>Completed by {viewRequestDetails.updatedByUser} on </> : <>Completed: </>}
						{DateUtils.FormatStringAsShortDate(viewRequestDetails.completedOn?.toString())}
					</CerebroText>
				)}
			</CerebroDrawer>
		</>
	);
};
